import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useIsAdmin } from "@/hooks/useIsAdmin";

interface ProtectedRouteProps {
  children: ReactNode;
  /** If true, requires the user to have the admin role. */
  requireAdmin?: boolean;
  /** Path to redirect unauthenticated users to. Defaults to /auth. */
  redirectTo?: string;
}

/**
 * Centralized route guard for private pages.
 *
 * - Waits for AuthContext bootstrap before deciding (prevents flicker / false redirects)
 * - Redirects unauthenticated users to /auth and preserves the original path in state
 * - Optionally enforces admin role using the cached useIsAdmin hook
 *
 * NOTE: This is a UI-level convenience guard. Real security is enforced by
 * Supabase RLS policies on the database. Never rely on this alone.
 */
export const ProtectedRoute = ({
  children,
  requireAdmin = false,
  redirectTo = "/auth",
}: ProtectedRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const location = useLocation();

  const stillLoading = authLoading || (requireAdmin && adminLoading);

  if (stillLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={redirectTo} replace state={{ from: location.pathname }} />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
